<template>
  <v-menu ref="menu" v-model="menu" :close-on-content-click="false" max-width="290" transition="scale-transition" offset-y>
    <template v-slot:activator="{ on, attrs }">
      <!-- eslint-disable-next-line prettier/prettier -->
      <v-text-field 
        v-model="range" 
        v-bind="attrs" 
        v-on="on" 
        :class="[clearable ? 'input-lg' : 'input-small']" 
        class="mr-1" 
        solo dense hide-details readonly
      >
        <template #append>
          <v-icon v-if="clearable" @click="onClear" :class="{ 'primary--text': menu }">mdi-close</v-icon>
          <v-icon class="icon" :class="{ 'primary--text': menu }" @click="on.click">mdi-calendar</v-icon>
        </template>
      </v-text-field>
    </template>
    <!-- eslint-disable-next-line prettier/prettier -->
    <v-date-picker 
      v-model="dates" 
      ref="datepicker" 
      @change="change" 
      :max="max" 
      @click:date="pickQuick" 
      @click:month="pickQuick" 
      @click:year="pickQuick" 
      :day-format="date => new Date(date).getDate()" 
      color="primary" 
      range no-title scrollable
     >
      <!-- Modified.by.KWShin.2026.10.15. 다른 기간 선택기처럼 맨 아래에 '선택 완료' 버튼을 둔다 -->
      <v-card>
        <v-btn-toggle v-if="!disabledQuickPick" v-model="mode" class="d-flex" dense>
          <v-btn small @click="setType('DATE')">{{ $t('components.dtrp.day') }}</v-btn>
          <v-btn small @click="setType('DATE')">{{ $t('components.dtrp.week') }}</v-btn>
          <v-btn small @click="setType('MONTH')">{{ $t('components.dtrp.month') }}</v-btn>
          <v-btn small @click="setType('MONTH')">{{ $t('components.dtrp.quarter') }}</v-btn>
          <v-btn small @click="setType('YEAR')">{{ $t('components.dtrp.year') }}</v-btn>
        </v-btn-toggle>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="menu = false">
            {{ $t('common.select.done') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-date-picker>
  </v-menu>
</template>

<script>
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import isEqual from 'date-fns/isEqual';
import isBefore from 'date-fns/isBefore';
import addHours from 'date-fns/addHours';
import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';
import startOfWeek from 'date-fns/startOfWeek';
import lastDayOfWeek from 'date-fns/lastDayOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import startOfQuarter from 'date-fns/startOfQuarter';
import endOfQuarter from 'date-fns/endOfQuarter';
import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';
import min from 'date-fns/min';
import set from 'date-fns/set';

const DAY_MODE = 0;
const WEEK_MODE = 1;
const MONTH_MODE = 2;
const QUARTER_MODE = 3;
const YEAR_MODE = 4;
/*
 * max 값 계산해서 디스플레이 및 emit 등에 적용
 */
export default {
  props: {
    value: {
      type: Array,
      default: () => {
        return [startOfDay(new Date()), endOfDay(new Date())];
      },
    },
    disabledQuickPick: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menu: false,
      mode: null,
      max: format(new Date(), 'yyyy-MM-dd'),
      dates: this.getDates(),
      pattern: 'yyyy-MM-dd',
      localizedPattern: 'yyyy-MM-dd',
      origin: [],
    };
  },
  computed: {
    range() {
      const range = [];
      if (this.getBeginDate() !== null) {
        range.splice(0, 1, format(this.getBeginDate(), this.pattern));
      }
      if (this.getEndDate() !== null) {
        range.splice(1, 1, format(this.getEndDate(), this.pattern));
      }
      return range.join(' ~ ');
    },
    begin() {
      return this.getBeginDate() !== null ? format(this.getBeginDate(), this.localizedPattern) : '---';
    },
    end() {
      return this.getEndDate() !== null ? format(this.getEndDate(), this.localizedPattern) : '---';
    },
  },
  watch: {
    value() {
      this.dates = this.getDates();
    },
    menu(value) {
      if (!value) {
        if (this.getBeginDate() === null && this.getEndDate() === null) {
          this.emit(null);
        } else if (this.getEndDate() === null || isBefore(this.getEndDate(), this.getBeginDate())) {
          this.setEndDate(addHours(this.getBeginDate(), 1));
          this.emit();
        } else if (!isEqual(this.origin[0], this.getBeginDate()) || !isEqual(this.origin[1], this.getEndDate())) {
          this.emit();
        }
      }
    },
  },
  created() {
    this.origin = [this.getBeginDate(), this.getEndDate()];
  },
  methods: {
    getBeginDate() {
      return this.getDate(0);
    },
    setBeginDate(date) {
      this.setDate(0, date);
    },
    getEndDate() {
      return this.getDate(1);
    },
    setEndDate(date) {
      const md = min([parseISO(format(date, 'yyyy-MM-dd')), parseISO(this.max)]);
      date = set(date, { year: md.getFullYear(), month: md.getMonth(), date: md.getDate() });
      this.setDate(1, date);
    },
    getDate(index) {
      let date = this.dates[index];
      return date !== undefined ? parseISO(date) : null;
    },
    setDate(index, date) {
      this.dates.splice(index, 1, format(date, 'yyyy-MM-dd'));
    },
    change() {
      if (isBefore(this.getEndDate(), this.getBeginDate())) {
        this.dates.reverse();
      }
    },
    emit(value = [this.getBeginDate(), this.getEndDate()]) {
      this.$emit('input', value);
      this.$emit('change', value);
    },
    setType(type) {
      this.$refs.datepicker.activePicker = type;
    },
    pickQuick(value) {
      if (this.mode != null) {
        const date = parseISO(this.mode === YEAR_MODE ? value + '-01-01' : value);
        let dates;
        if (this.mode === DAY_MODE) {
          dates = [date, date];
        }
        if (this.mode === WEEK_MODE) {
          dates = [startOfWeek(date), lastDayOfWeek(date)];
        }
        if (this.mode === MONTH_MODE) {
          dates = [startOfMonth(date), endOfMonth(date)];
        }
        if (this.mode === QUARTER_MODE) {
          dates = [startOfQuarter(date), endOfQuarter(date)];
        }
        if (this.mode === YEAR_MODE) {
          dates = [startOfYear(date), endOfYear(date)];
        }
        this.setBeginDate(startOfDay(dates[0]));
        this.setEndDate(endOfDay(dates[1]));
        this.mode = null;
        this.setType('DATE');
      }
    },
    getDates() {
      if (this.value !== null) {
        return [format(this.value[0], 'yyyy-MM-dd'), format(this.value[1], 'yyyy-MM-dd')];
      }
      return [];
    },
    onClear() {
      this.emit(null);
      this.$emit('click:clear');
    },
  },
};
</script>

<style lang="scss" scoped>
.v-picker ::v-deep .v-picker__actions {
  padding: 0;
}
.input-lg {
  width: 215px;
}
.input-small {
  width: 185px;
}
.print-section {
  margin: 0 0 5px 10px;
}
.v-btn-toggle {
  border-radius: 0 0 4px 4px;
}
.v-btn:not(.v-btn--round).v-size--small {
  min-width: 58px;
}
.icon {
  padding: 0 !important;
}
</style>
